/**
 * Session idle timeout — Moodle-style "sign out after N minutes without user
 * activity".
 *
 * Before this, the tenant's "Session idle timeout" setting only shortened the
 * access token; the 7-day refresh token renewed it silently, so a browser left
 * open overnight was still signed in the next morning.
 *
 * - The timeout comes from the tenant Security setting, carried as the
 *   `idle_min` claim on the access token (fallback 30 min).
 * - Only real user interaction counts as activity: pointer, keyboard, touch,
 *   wheel, media playback, or focus inside an embedded player (SCORM/H5P/Studio
 *   iframes don't bubble events to us). Background polling and the messaging
 *   socket do NOT count — otherwise an idle tab would never time out.
 * - Activity is shared across tabs (localStorage), like one Moodle session.
 * - Enforced on load (an expired session never reaches the UI), on a timer, and
 *   before any silent token refresh (axiosInstance).
 */
import { jwtDecode } from 'jwt-decode';
import { tokenStorage } from '@/api/tokenManager';

const LAST_ACTIVITY_KEY = 'ilearn_last_activity';
const DEFAULT_IDLE_MIN = 30;
const WRITE_THROTTLE_MS = 15_000;
const CHECK_EVERY_MS = 30_000;

export function idleTimeoutMs(): number {
  const token = tokenStorage.getAccessToken();
  if (token) {
    try {
      const claims = jwtDecode<{ idle_min?: number }>(token);
      if (typeof claims.idle_min === 'number' && claims.idle_min > 0) return claims.idle_min * 60_000;
    } catch {
      // malformed token — use the default
    }
  }
  return DEFAULT_IDLE_MIN * 60_000;
}

function readLastActivity(): number | null {
  try {
    const v = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

let lastWrite = 0;
export function markActivity(force = false): void {
  const now = Date.now();
  if (!force && now - lastWrite < WRITE_THROTTLE_MS) return;
  lastWrite = now;
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
  } catch {
    // storage unavailable — the timer simply can't fire
  }
}

/** True when a signed-in session has been idle longer than the timeout. */
export function isIdleExpired(): boolean {
  if (!tokenStorage.getAccessToken() && !tokenStorage.getRefreshToken()) return false;
  const last = readLastActivity();
  // No record yet (e.g. first load after this shipped): treat as active rather
  // than signing everyone out on deploy.
  if (last === null) return false;
  return Date.now() - last > idleTimeoutMs();
}

function clearSession(): void {
  tokenStorage.clearTokens();
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    localStorage.removeItem('is_impersonating');
    localStorage.removeItem('original_admin_name');
  } catch {
    // ignore
  }
}

/** Call before the app renders: an idle-expired session never reaches the UI. */
export function enforceIdleOnLoad(): void {
  if (isIdleExpired()) clearSession();
}

/** Sign out now because of inactivity (timer, or a refresh attempted while idle). */
export function expireIdleSession(): void {
  clearSession();
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login?expired=1';
  }
}

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'mousemove'] as const;

/** Track activity and sign out after the idle timeout. Returns a cleanup fn. */
export function startIdleWatcher(): () => void {
  // The session was validated on load (enforceIdleOnLoad), and the (re)load is
  // itself a user action.
  markActivity(true);
  const onActivity = () => markActivity();
  ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
  // Media events don't bubble; listen in the capture phase.
  window.addEventListener('timeupdate', onActivity, { capture: true, passive: true });

  const timer = window.setInterval(() => {
    // Interaction inside an embedded player never reaches this window — count
    // "page visible and focused inside an iframe" as activity.
    if (
      document.visibilityState === 'visible' &&
      document.hasFocus() &&
      document.activeElement?.tagName === 'IFRAME'
    ) {
      markActivity();
    }
    if (isIdleExpired()) expireIdleSession();
  }, CHECK_EVERY_MS);

  return () => {
    ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    window.removeEventListener('timeupdate', onActivity, { capture: true });
    window.clearInterval(timer);
  };
}
